import { Type, type Static } from '@sinclair/typebox';
import { CompanyKey, Role, UserStructures } from './companies';
import { createSchema } from '../../util/schema';

const hfUserProfileProps = {
  email: Type.String({ format: 'email' }),
  fullName: Type.String(),
  firstName: Type.String(),
  lastName: Type.String(),
  userId: Type.Integer({ description: 'HedgeFacts UserID' }),
  internal: Type.Boolean({ description: 'true when legacy UserType is HEDGE_FACTS_INTERNAL (HF staff)' }),
  contextId: Type.Integer(),
  disabled: Type.Boolean(),
  company: Type.String(),
  addr1: Type.String(),
  addr2: Type.String(),
  city: Type.String(),
  state: Type.String(),
  postalCode: Type.String(),
  dateCreated: Type.String({ description: 'HedgeFacts DateCreated' }),
  createdBy: Type.Integer({ description: 'HedgeFacts CreatedBy user id' }),
};

export const HfUserProfile = Type.Object(hfUserProfileProps, { $id: 'HfUserProfile' });
export type HfUserProfile = Static<typeof HfUserProfile>;
export const hfUserProfileSchema = createSchema(HfUserProfile);

const hashedUserId = Type.String({ minLength: 1, description: 'Hashed HedgeFacts UserID (same token as public resource ids).' });

/**
 * Session origin — same values as Dynamo `Users.source` / legacy `isHFUser` mapping:
 * - `hedgefacts` = legacy HedgeFacts DB (SOAP; was `isHFUser: true`)
 * - `internal` = cloud Users table on AWS (was `isHFUser: false`)
 */
export const AuthSource = Type.Union([Type.Literal('hedgefacts'), Type.Literal('internal')], {
  description: 'hedgefacts = legacy SOAP DB; internal = cloud Users on AWS (not exposed as aws/dynamo)',
});
export type AuthSource = Static<typeof AuthSource>;

const tokenPairProps = {
  sub: hashedUserId,
  ticket: Type.String({ minLength: 1, description: 'AES-256-GCM encrypted HedgeFacts session ticket' }),
  datasource: Type.String({ minLength: 1, description: 'SOAP database name for hedgefacts sessions; companyKey for internal sessions.' }),
  source: AuthSource,
  companyKey: Type.Optional(CompanyKey),
  referralCompanyKey: Type.Optional(CompanyKey),
  isAggregator: Type.Optional(Type.Boolean({ description: 'Parent/aggregator company — hides client branding fields.' })),
  structures: Type.Optional(UserStructures),
  role: Type.Optional(Role),
  fundsEnabled: Type.Optional(
    Type.Boolean({ description: 'HedgeFacts Config HedgeFunds (`areFundsEnabled`). Set on hedgefacts logins; omitted for internal sessions.' }),
  ),
  managedAccountsEnabled: Type.Optional(
    Type.Boolean({
      description: 'HedgeFacts Config ManagedAccounts (`areManagedAccountsEnabled`). Set on hedgefacts logins; omitted for internal sessions.',
    }),
  ),
  email: Type.String({ format: 'email' }),
  firstName: Type.String(),
  lastName: Type.String(),
};

export const TokenPairInput = Type.Object(tokenPairProps, { $id: 'TokenPairInput' });
export type TokenPairInput = Static<typeof TokenPairInput>;
export const tokenPairInputSchema = createSchema(TokenPairInput);

export const HfJwtClaims = Type.Object(
  {
    ...tokenPairProps,
    iat: Type.Integer(),
    exp: Type.Integer(),
  },
  { $id: 'HfJwtClaims' },
);
export type HfJwtClaims = Static<typeof HfJwtClaims>;
export const hfJwtClaimsSchema = createSchema(HfJwtClaims);

/** Admin Center access and refresh JWTs. No role, datasource, or web session claims. */
export const AdminJwtClaims = Type.Object(
  {
    sub: hashedUserId,
    ticket: Type.String({ minLength: 1, description: 'AES-256-GCM encrypted Admin Central session ticket' }),
    firstName: Type.String(),
    lastName: Type.String(),
    isAdminCentral: Type.Literal(true),
    iat: Type.Integer(),
    exp: Type.Integer(),
  },
  { $id: 'AdminJwtClaims' },
);
export type AdminJwtClaims = Static<typeof AdminJwtClaims>;
export const adminJwtClaimsSchema = createSchema(AdminJwtClaims);

export const AdminAuthTokens = Type.Object(
  {
    accessToken: Type.String({ minLength: 1 }),
    refreshToken: Type.String({ minLength: 1 }),
    mfaTrustToken: Type.Optional(
      Type.String({
        minLength: 1,
        description:
          'MFA trust JWT (2h of inactivity; slides on refresh/login). Client stores it and sends it on the next password login to skip email 2FA. Email stays on this token, not the session JWT.',
      }),
    ),
  },
  { $id: 'AdminAuthTokens' },
);
export type AdminAuthTokens = Static<typeof AdminAuthTokens>;
export const adminAuthTokensSchema = createSchema(AdminAuthTokens);

export const HfRefreshClaims = Type.Object(
  {
    ticket: Type.String({ minLength: 1, description: 'AES-256-GCM encrypted HedgeFacts session ticket' }),
    sub: hashedUserId,
    datasource: Type.String({ minLength: 1, description: 'HedgeFacts client database name chosen at login' }),
    iat: Type.Integer(),
    exp: Type.Integer(),
  },
  { $id: 'HfRefreshClaims' },
);
export type HfRefreshClaims = Static<typeof HfRefreshClaims>;
export const hfRefreshClaimsSchema = createSchema(HfRefreshClaims);

export const LoginChallengeClaims = Type.Object(
  {
    purpose: Type.Literal('mfa_challenge'),
    sub: hashedUserId,
    email: Type.String({ format: 'email' }),
    jti: Type.String({ minLength: 1, description: 'Challenge id (matches AuthOtp pk)' }),
    iat: Type.Integer(),
    exp: Type.Integer(),
  },
  { $id: 'LoginChallengeClaims' },
);
export type LoginChallengeClaims = Static<typeof LoginChallengeClaims>;
export const loginChallengeClaimsSchema = createSchema(LoginChallengeClaims);

export const MfaTrustClaims = Type.Object(
  {
    purpose: Type.Literal('mfa_trust'),
    sub: hashedUserId,
    email: Type.String({ format: 'email' }),
    datasource: Type.String({ minLength: 1 }),
    iat: Type.Integer(),
    exp: Type.Integer(),
  },
  { $id: 'MfaTrustClaims' },
);
export type MfaTrustClaims = Static<typeof MfaTrustClaims>;
export const mfaTrustClaimsSchema = createSchema(MfaTrustClaims);

export const LoginRequest = Type.Object(
  {
    email: Type.String({ format: 'email', description: 'User email' }),
    password: Type.String({ minLength: 1, description: 'User password' }),
    datasource: Type.Optional(Type.String({ minLength: 1, description: 'Required when useDatasource is true.' })),
    useDatasource: Type.Optional(Type.Boolean({ description: 'true = try SOAP first, then Dynamo. false/absent = Dynamo only.' })),
    mfaTrustToken: Type.Optional(
      Type.String({
        minLength: 1,
        description:
          'Optional MFA trust JWT from a recent successful 2FA (or renewed activity). When valid for this user, login skips the email code step.',
      }),
    ),
  },
  { $id: 'LoginRequest' },
);
export type LoginRequest = Static<typeof LoginRequest>;
export const loginRequestSchema = createSchema(LoginRequest);

/** Admin Center login. Username is the FACTSMaster.AdminUsers.AdminUser value; no client datasource. */
export const AdminLoginRequest = Type.Object(
  {
    username: Type.String({ minLength: 1, description: 'Admin Central user name' }),
    password: Type.String({ minLength: 1, description: 'Admin Central password' }),
    mfaTrustToken: Type.Optional(
      Type.String({
        minLength: 1,
        description:
          'Optional MFA trust JWT from a recent Admin Center 2FA (or renewed activity). When valid for this admin user, login skips the email code step.',
      }),
    ),
  },
  { $id: 'AdminLoginRequest' },
);
export type AdminLoginRequest = Static<typeof AdminLoginRequest>;
export const adminLoginRequestSchema = createSchema(AdminLoginRequest);

export const LoginChallenge = Type.Object(
  {
    challengeToken: Type.String({ minLength: 1, description: 'Short-lived MFA challenge JWT (not a session)' }),
    maskedEmail: Type.String({ minLength: 1, description: 'Email destination with local-part masked' }),
    expiresIn: Type.Integer({ minimum: 1, description: 'Seconds until the emailed code and challenge expire' }),
  },
  { $id: 'LoginChallenge' },
);
export type LoginChallenge = Static<typeof LoginChallenge>;
export const loginChallengeSchema = createSchema(LoginChallenge);

export const VerifyLogin2faRequest = Type.Object(
  {
    challengeToken: Type.String({ minLength: 1 }),
    code: Type.String({ minLength: 6, maxLength: 6, pattern: '^[0-9]{6}$', description: '6-digit one-time code from email' }),
  },
  { $id: 'VerifyLogin2faRequest' },
);
export type VerifyLogin2faRequest = Static<typeof VerifyLogin2faRequest>;
export const verifyLogin2faRequestSchema = createSchema(VerifyLogin2faRequest);

export const ResendLogin2faRequest = Type.Object(
  {
    challengeToken: Type.String({ minLength: 1 }),
  },
  { $id: 'ResendLogin2faRequest' },
);
export type ResendLogin2faRequest = Static<typeof ResendLogin2faRequest>;
export const resendLogin2faRequestSchema = createSchema(ResendLogin2faRequest);

export const AuthTokens = Type.Object(
  {
    accessToken: Type.String({ minLength: 1 }),
    refreshToken: Type.String({ minLength: 1 }),
    mfaTrustToken: Type.String({
      minLength: 1,
      description:
        'MFA trust JWT (2h of inactivity; slides on refresh/login). Client stores it and sends it on the next password login to skip email 2FA.',
    }),
  },
  { $id: 'AuthTokens' },
);
export type AuthTokens = Static<typeof AuthTokens>;
export const authTokensSchema = createSchema(AuthTokens);

/** `/auth/refresh` preserves the web or Admin Center token shape presented by the caller. */
export const RefreshTokens = Type.Union([AuthTokens, AdminAuthTokens], { $id: 'RefreshTokens' });
export type RefreshTokens = Static<typeof RefreshTokens>;
export const refreshTokensSchema = createSchema(RefreshTokens);

/** Password login returns either an MFA challenge or session tokens (when MFA trust is still valid). */
export const LoginResult = Type.Union([LoginChallenge, AuthTokens], { $id: 'LoginResult' });
export type LoginResult = Static<typeof LoginResult>;
export const loginResultSchema = createSchema(LoginResult);

/** Admin Center password login returns either an MFA challenge or session tokens (when MFA trust is still valid). */
export const AdminLoginResult = Type.Union([LoginChallenge, AdminAuthTokens], { $id: 'AdminLoginResult' });
export type AdminLoginResult = Static<typeof AdminLoginResult>;
export const adminLoginResultSchema = createSchema(AdminLoginResult);

export const RefreshHeaders = Type.Object(
  {
    authorization: Type.String({ minLength: 1, description: 'Bearer <refresh JWT>' }),
    'x-expired-token': Type.String({ minLength: 1, description: 'Expired access JWT' }),
    'x-mfa-trust': Type.Optional(
      Type.String({
        minLength: 1,
        description: 'Admin Center MFA trust JWT. When it matches the admin user, refresh slides the 2h skip-2FA window. Omitted by the web app.',
      }),
    ),
  },
  { $id: 'RefreshHeaders' },
);
export type RefreshHeaders = Static<typeof RefreshHeaders>;
export const refreshHeadersSchema = createSchema(RefreshHeaders);

export const ChangePasswordInput = Type.Object(
  {
    currentPassword: Type.String({ minLength: 1, description: 'Current password' }),
    newPassword: Type.String({ minLength: 1, description: 'New password. Strength rules are enforced by HedgeFacts, not this contract.' }),
  },
  { $id: 'ChangePasswordInput' },
);
export type ChangePasswordInput = Static<typeof ChangePasswordInput>;
export const changePasswordInputSchema = createSchema(ChangePasswordInput);

/** Cloud / partner passwords. SOAP change-password stays on HedgeFacts rules. */
export const NEW_PASSWORD_MIN_LENGTH = 8;
export const NEW_PASSWORD_HINT = 'At least 8 characters, with upper and lower case, a number, and a symbol';

export function passwordPolicyError(password: string): string | undefined {
  if (password.length < NEW_PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${NEW_PASSWORD_MIN_LENGTH} characters`;
  }
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return 'Password must include upper and lower case, a number, and a symbol';
  }
  return undefined;
}

const NewPassword = Type.String({
  minLength: NEW_PASSWORD_MIN_LENGTH,
  pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).+$',
  description: NEW_PASSWORD_HINT,
});

export const PasswordStartRequest = Type.Object(
  {
    email: Type.String({
      format: 'email',
      description:
        'Cloud user email. Always returns a challenge-shaped 200 when rate limits allow; unknown addresses get a disposable challenge (no email sent).',
    }),
  },
  { $id: 'PasswordStartRequest' },
);
export type PasswordStartRequest = Static<typeof PasswordStartRequest>;
export const passwordStartRequestSchema = createSchema(PasswordStartRequest);

export const PasswordCompleteRequest = Type.Object(
  {
    challengeToken: Type.String({ minLength: 1 }),
    code: Type.String({ minLength: 6, maxLength: 6, pattern: '^[0-9]{6}$', description: '6-digit one-time code from email' }),
    newPassword: NewPassword,
  },
  { $id: 'PasswordCompleteRequest' },
);
export type PasswordCompleteRequest = Static<typeof PasswordCompleteRequest>;
export const passwordCompleteRequestSchema = createSchema(PasswordCompleteRequest);
